export enum WorkflowNodeType {
  Trigger = 'trigger',
  If = 'if',
  Say = 'say',
  CreateConversationForPhoneCall = 'createConversationForPhoneCall',
  Play = 'play',
  SetVariables = 'setVariables',
  SendToFlexUI = 'sendToFlexUI',
  ConnectCallTo = 'connectCallTo',
}

export type WorkflowNodeData =
  | {
      type: WorkflowNodeType.Trigger;
      data?: undefined;
    }
  | {
      type: WorkflowNodeType.If;
      data?: {
        operand?: string;
        operator?: string;
        value?: string | number | boolean;
      };
    }
  | {
      type: WorkflowNodeType.Say;
      data?: {
        message?: string;
      };
    }
  | {
      type: WorkflowNodeType.CreateConversationForPhoneCall;
      data?: Record<string, unknown>;
    }
  | {
      type: WorkflowNodeType.Play;
      data?: {
        url?: string;
      };
    }
  | {
      type: WorkflowNodeType.SetVariables;
      data?: {
        variables?: Record<string, string>;
      };
    }
  | {
      type: WorkflowNodeType.SendToFlexUI;
      data?: Record<string, unknown>;
    }
  | {
      type: WorkflowNodeType.ConnectCallTo;
      data?: {
        destination?: string;
      };
    }
  | {
      type: WorkflowNodeType;
      data?: Record<string, unknown>;
    };

export interface WorkflowDefinitionNode
  extends Partial<Omit<WorkflowNodeData, 'type' | 'data'>> {
  id: string;
  type: WorkflowNodeType;
  data?: WorkflowNodeData['data'];
  position?: {
    x: number;
    y: number;
  };
}

export interface ParsedNodeShape {
  id: string;
  type: string;
  data?: Record<string, unknown>;
  position?: { x: number; y: number };
}

export interface ParsedEdgeShape {
  id?: string;
  source: string;
  target: string;
  data?: {
    condition?: boolean;
  };
}

export interface WorkflowDefinitionEdge {
  id?: string;
  source: string;
  target: string;
  data?: {
    condition?: boolean;
  };
}

export interface WorkflowDefinition {
  nodes: WorkflowDefinitionNode[];
  edges: WorkflowDefinitionEdge[];
}

export interface ParsedWorkflowDefinition {
  definition: WorkflowDefinition | null;
  error: string | null;
}

export const isWorkflowDefinitionShape = (
  definition: unknown,
): definition is { nodes: unknown[]; edges: unknown[] } => {
  if (typeof definition !== 'object' || definition === null) {
    return false;
  }

  const candidate = definition as { nodes?: unknown; edges?: unknown };
  return Array.isArray(candidate.nodes) && Array.isArray(candidate.edges);
};

export const isValidNodeShape = (node: unknown): node is ParsedNodeShape => {
  if (typeof node !== 'object' || node === null) {
    return false;
  }
  const candidate = node as { id?: unknown; type?: unknown };
  return typeof candidate.id === 'string' && typeof candidate.type === 'string';
};

export const isValidEdgeShape = (edge: unknown): edge is ParsedEdgeShape => {
  if (typeof edge !== 'object' || edge === null) {
    return false;
  }
  const candidate = edge as { source?: unknown; target?: unknown };
  return typeof candidate.source === 'string' && typeof candidate.target === 'string';
};

export const isWorkflowNodeType = (value: string): value is WorkflowNodeType =>
  Object.values<WorkflowNodeType>(WorkflowNodeType).includes(value as WorkflowNodeType);
